import React, { useEffect, useState } from 'react';
import { Scissors, Wallet, Plus, Download } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Badge, Th, Td, StatCard, EmptyState } from '../components/ui';
import { fmtMoney, fmtNum, fmtDate, statusColor, groupCutLogsByDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import CutterSummaryPrint from '../components/CutterSummaryPrint';
import { downloadElementAsPdf } from '../lib/pdf';

export default function MyCutting() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [cut, setCut] = useState(null);
  const [rollLogs, setRollLogs] = useState([]);
  const [logOpen, setLogOpen] = useState(false);

  const load = () => {
    api.get('/jobs').then((r) => setJobs(r.data));
    api.get('/cutter-payments').then((r) => setPayments(r.data));
    api.get('/products').then((r) => setProducts(r.data));
    api.get('/raw-materials').then((r) => setMaterials(r.data));
    api.get('/cutting-logs').then((r) => setRollLogs(r.data));
  };
  useEffect(() => { load(); }, []);

  const pname = (id) => products.find((p) => p.id === id)?.name || '—';
  const myCut = jobs.filter((j) => j.cutById === user.id);
  const pending = jobs.filter((j) => !j.issuedMaterialId && j.status !== 'completed');
  const totalPaid = payments.reduce((s, p) => s + p.amount, 0);
  const totalRolls = rollLogs.reduce((s, l) => s + (l.rolls || 0), 0);
  const totalEarned = rollLogs.reduce((s, l) => s + (l.earnings || 0), 0);

  return (
    <div>
      <PageHeader title="My Cutting" subtitle={`${user.name} · Cutting jobs, rolls & payments`}
        actions={<Button onClick={() => setLogOpen(true)} data-testid="log-rolls-btn"><Plus size={16} /> Log Rolls Cut</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Jobs Cut by Me" value={fmtNum(myCut.length)} />
        <StatCard label="Awaiting Cutting" value={fmtNum(pending.length)} accent="text-[#B45309]" />
        <StatCard label="Rolls Cut" value={fmtNum(totalRolls)} />
        <StatCard label="Total Earned" value={fmtMoney(totalEarned)} />
        <StatCard label="Total Received" value={fmtMoney(totalPaid)} accent="text-emerald-600" />
      </div>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">My Roll Cutting Log</p>
      <Card className="overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Date</Th><Th>Product</Th><Th right>Rolls</Th><Th right>Length (Yards)</Th><Th right>Dozens</Th><Th right>Earnings</Th><Th>Notes</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rollLogs.map((l) => (
                <tr key={l.id} data-testid="roll-log-row" className="hover:bg-slate-50/60">
                  <Td>{fmtDate(l.date)}</Td>
                  <Td className="text-slate-600">{l.productName || '—'}</Td>
                  <Td right className="num">{fmtNum(l.rolls)}</Td>
                  <Td right className="num">{fmtNum(l.rollLengthYards || 0)}</Td>
                  <Td right className="num">{fmtNum(l.dozens || 0)}</Td>
                  <Td right className="num font-semibold">{fmtMoney(l.earnings)}</Td>
                  <Td className="text-slate-500">{l.notes || '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!rollLogs.length && <EmptyState title="No rolls logged yet" description="Tap 'Log Rolls Cut' after cutting to record your work." icon={Scissors} />}
      </Card>

      <MyDatewiseCutting logs={rollLogs} cutterName={user.name} />

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Cutting Jobs</p>
      <Card className="overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Job #</Th><Th>Product</Th><Th>Size</Th><Th right>Qty</Th><Th right>Fabric Issued</Th><Th>Status</Th><Th className="text-right">Action</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((j) => (
                <tr key={j.id} data-testid="mycutting-job-row" className="hover:bg-slate-50/60 transition-colors">
                  <Td><span className="font-mono text-xs font-semibold">{j.jobNumber}</span><span className="block text-xs text-slate-400">{fmtDate(j.date)}</span></Td>
                  <Td className="font-medium text-slate-800">{pname(j.productId)}</Td>
                  <Td><Badge className="bg-slate-100 border-slate-200 text-slate-600">{j.size || 'Universal'}</Badge></Td>
                  <Td right>{fmtNum(j.quantityAssigned)}</Td>
                  <Td right>{j.issuedMaterialQty ? fmtNum(j.issuedMaterialQty) : '—'}</Td>
                  <Td><Badge className={statusColor(j.status)}>{j.status.replace('_', ' ')}</Badge></Td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {!j.issuedMaterialId && j.status !== 'completed' && (
                      <Button size="sm" variant="accent" onClick={() => setCut(j)} data-testid="mycutting-cut-btn"><Scissors size={13} /> Cut & Issue</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!jobs.length && <EmptyState title="No cutting jobs" description="Jobs assigned for cutting will appear here." icon={Scissors} />}
      </Card>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">My Payments</p>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Date</Th><Th right>Amount</Th><Th>Notes</Th><Th>Paid By</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((p) => (
                <tr key={p.id} data-testid="mycutting-payment-row" className="hover:bg-slate-50/60 transition-colors">
                  <Td>{fmtDate(p.date)}</Td>
                  <Td right className="text-emerald-600 font-semibold">{fmtMoney(p.amount)}</Td>
                  <Td className="text-slate-500">{p.notes || '—'}</Td>
                  <Td className="text-xs text-slate-500">{p.paidBy}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!payments.length && <EmptyState title="No payments yet" description="Payments recorded for your cutting work will appear here." icon={Wallet} />}
      </Card>

      {cut && <CutModal job={cut} materials={materials} onClose={() => setCut(null)} onSaved={() => { setCut(null); load(); }} />}
      {logOpen && <RollLogModal onClose={() => setLogOpen(false)} onSaved={() => { setLogOpen(false); load(); }} />}
    </div>
  );
}

function MyDatewiseCutting({ logs, cutterName }) {
  const [month, setMonth] = useState('');
  const [company, setCompany] = useState(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => { api.get('/settings').then((r) => setCompany(r.data?.company)).catch(() => {}); }, []);

  const filtered = month ? logs.filter((l) => (l.date || '').startsWith(month)) : logs;
  const rows = groupCutLogsByDate(filtered);
  const totals = rows.reduce((a, r) => ({ rolls: a.rolls + r.rolls, yards: a.yards + r.yards, dozens: a.dozens + r.dozens, amount: a.amount + r.amount }), { rolls: 0, yards: 0, dozens: 0, amount: 0 });
  const periodLabel = month ? new Date(`${month}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'All Time';

  const download = async () => {
    setDownloading(true);
    try {
      const name = cutterName.replace(/[^\w\u0600-\u06FF\s-]/g, '').trim().replace(/\s+/g, '-');
      await downloadElementAsPdf(document.getElementById('mycutting-summary-print'), `${name}-Cutting-${month || 'All'}.pdf`);
    } catch { toast.error('Could not generate PDF'); }
    setDownloading(false);
  };

  return (
    <>
      <div className="flex items-end justify-between mb-2">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">My Date-wise Cutting</p>
        <div className="flex items-end gap-2">
          <Input label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="mycutting-month-input" />
          {month && <Button size="sm" variant="outline" onClick={() => setMonth('')}>All Time</Button>}
          <Button size="sm" variant="outline" onClick={download} disabled={downloading} data-testid="mycutting-download-pdf-btn"><Download size={14} /> {downloading ? 'Generating…' : 'PDF'}</Button>
        </div>
      </div>
      <Card className="overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Date</Th><Th right>Rolls</Th><Th right>Length (Yards)</Th><Th right>Dozens</Th><Th right>Amount</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.date} data-testid="mycutting-datewise-row" className="hover:bg-slate-50/60">
                  <Td className="font-medium text-slate-800">{fmtDate(r.date)}</Td>
                  <Td right className="num font-semibold">{fmtNum(r.rolls)}</Td>
                  <Td right className="num">{fmtNum(r.yards)}</Td>
                  <Td right className="num">{fmtNum(r.dozens)}</Td>
                  <Td right className="num">{fmtMoney(r.amount)}</Td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <Td className="font-bold text-slate-800">Total ({periodLabel})</Td>
                  <Td right className="num font-bold">{fmtNum(totals.rolls)}</Td>
                  <Td right className="num font-bold">{fmtNum(totals.yards)}</Td>
                  <Td right className="num font-bold">{fmtNum(totals.dozens)}</Td>
                  <Td right className="num font-bold">{fmtMoney(totals.amount)}</Td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {!rows.length && <EmptyState title="No cutting yet" icon={Scissors} />}
      </Card>
      <div style={{ position: 'fixed', left: '-20000px', top: 0 }}>
        <div id="mycutting-summary-print">
          <CutterSummaryPrint cutterName={cutterName} rows={rows} totals={totals} periodLabel={periodLabel} company={company} />
        </div>
      </div>
    </>
  );
}

function RollLogModal({ onClose, onSaved }) {
  const [f, setF] = useState({ rolls: '', date: new Date().toISOString().slice(0, 10), notes: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.post('/cutting-logs', { rolls: Number(f.rolls), date: f.date, notes: f.notes });
      toast.success('Rolls logged'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title="Log Rolls Cut" onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-roll-log-btn">Save</Button></>}>
      <div className="space-y-4">
        <Input label="Date" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} data-testid="roll-log-date" />
        <Input label="How many rolls did you cut?" type="number" step="0.5" value={f.rolls} onChange={(e) => set('rolls', e.target.value)} data-testid="roll-log-qty" />
        <Input label="Notes" value={f.notes} onChange={(e) => set('notes', e.target.value)} />
      </div>
    </Modal>
  );
}

function CutModal({ job, materials, onClose, onSaved }) {
  const [materialId, setMaterialId] = useState(materials[0]?.id || '');
  const [quantity, setQuantity] = useState(0);
  const save = async () => {
    try {
      await api.post(`/jobs/${job.id}/cut`, { materialId, quantity: Number(quantity) });
      toast.success('Fabric issued · job in progress'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={`Cut & Issue Fabric · ${job.jobNumber}`} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="accent" onClick={save} data-testid="mycutting-save-cut-btn">Issue Fabric</Button></>}>
      <div className="space-y-4">
        <Select label="Fabric / Material" value={materialId} onChange={(e) => setMaterialId(e.target.value)} data-testid="mycutting-material">
          {materials.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.currentStock} {m.unit})</option>)}
        </Select>
        <Input label="Quantity to cut" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="mycutting-qty" />
      </div>
    </Modal>
  );
}
